import { startTransition, useCallback } from 'react';
import cn from 'clsx';
import { type WithTestId } from '@pkgs/components/types/types';
import { useShopFilterRemovalMessage } from '@pkgs/components/hooks/useShopFilterRemovalMessage';
import { useLocationFilterInitialLocation } from '@pkgs/components/hooks/useLocationFilterInitialLocation';
import { useIsLipaPage } from '@pkgs/components/hooks/useIsLipaPage';
import { useAppContext } from '@pkgs/components/hooks/useAppContext';
import { useSearchStore } from '@pkgs/components/hooks/stores/search/useSearchStore';
import { SearchMode } from '@pkgs/components/hooks/stores/search/slices/searchModeSlice';

import { ExtendedRadiusFilter } from '../ExtendedSearchRadiusFilters/ExtendedRadiusFilter/ExtendedRadiusFilter';
import { AutoComplete, type AutoSuggestOption } from '../AutoComplete/AutoComplete';
import { DesktopAiLocationFilter } from '../AiLocationFilter/DesktopAiLocationFilter';

import locationFilterStyles from './LocationFilter.module.css';
import styles from './DesktopLocationFilter.module.css';

export type LocationFilterProps = WithTestId<{
  className?: string;
}>;

export const DesktopLocationFilter = (props: LocationFilterProps) => {
  const { featureFlags } = useAppContext();
  const setPlaceIds = useSearchStore((state) => state.setPlaceIds);
  const setPlaceName = useSearchStore((state) => state.setPlaceName);
  const searchMode = useSearchStore((state) => state.searchMode);
  const isLipaPage = useIsLipaPage();
  const { removeShopFilterWithUndoMessage } = useShopFilterRemovalMessage();

  const handleSelection = useCallback(
    (selectedLocation: AutoSuggestOption) => {
      startTransition(() => {
        if (isLipaPage) {
          removeShopFilterWithUndoMessage();
        }
        setPlaceIds([selectedLocation.id]);
        setPlaceName(selectedLocation.title);
      });
    },
    [isLipaPage, removeShopFilterWithUndoMessage, setPlaceIds, setPlaceName],
  );

  const initialLocation = useLocationFilterInitialLocation({ visibleSearchMode: searchMode });

  if (featureFlags.isAiSearchEnabled && searchMode === SearchMode.AI) {
    return <DesktopAiLocationFilter />;
  }

  return (
    <div
      className={locationFilterStyles.locationFilterContainer}
      data-testid="search-components_location-filter_desktop"
      data-ai-search-enabled={featureFlags?.isAiSearchEnabled}
      data-location-filter-container
    >
      <div
        className={cn(locationFilterStyles.autoCompleteContainer, styles.desktopAutoCompleteContainer)}
        data-ai-search-enabled={featureFlags?.isAiSearchEnabled}
      >
        <AutoComplete onSelectionChange={handleSelection} initialLocation={initialLocation} {...props} />
        <div className={styles.divider} />
        <ExtendedRadiusFilter
          data-hidden={!initialLocation}
          className={styles.extendedRadiusFilter}
          hasLabelHidden
          data-testid="search-components_filter-bar_search-radius-filter"
        />
      </div>
    </div>
  );
};
